<script setup>
import { Link, useForm, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    users: Object,
    filters: Object,
    flash: Object,
    user: Object,
    permissions: Object,
    companies: Object,
    agents: Object,
    sessions: Object
});

let userSearch = ref(props.filters.userSearch);
const $toast = useToast();
dayjs.extend(advancedFormat);
let isLoading = ref(false);

watch(userSearch, debounce(function (value) {
    router.get('/users', { userSearch: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 700));

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

//function to check confirm password
const checkPassword = () => {
    if (UserAddform.firstpassword != UserAddform.password) {
        UserAddform.errors.password = 'Password does not match';
    } else {
        UserAddform.errors.password = '';
    }
};

const UserAddform = useForm({
    name: '',
    email: '',
    password: '',
    firstpassword: '',
    user_type: '',
    type_id: '',
});

const UserAddformSubmit = () => {
    UserAddform.post('/users/store', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            document.getElementById('addUserOffcanvas').classList.remove('show');
            document.getElementsByClassName('offcanvas-backdrop')[0].classList.remove('show');
            $toast.open({
                message: props.flash.message,
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
};

//function to convert unix timestamp
function convertTimestamp(timestamp) {
    var d = new Date(timestamp * 1000),	// Convert the passed timestamp to milliseconds
        yyyy = d.getFullYear(),
        mm = ('0' + (d.getMonth() + 1)).slice(-2),	// Months are zero based. Add leading 0.
        dd = ('0' + d.getDate()).slice(-2),			// Add leading 0.
        hh = d.getHours(),
        h = hh,
        min = ('0' + d.getMinutes()).slice(-2),		// Add leading 0.
        ampm = 'AM',
        time;

    if (hh > 12) {
        h = hh - 12;
        ampm = 'PM';
    } else if (hh === 12) {
        h = 12;
        ampm = 'PM';
    } else if (hh == 0) {
        h = 12;
    }

    // ie: 2013-02-18, 8:35 AM	
    time = yyyy + '-' + mm + '-' + dd + ', ' + h + ':' + min + ' ' + ampm;

    return time;
}
//function to convert date time to times ago
function timeAgo(time) {

    //if time is in unix timestamp format
    if (typeof time === 'number') {
        time = convertTimestamp(time);
    }

    //if time is in string format
    if (typeof time === 'string') {
        time = new Date(time);
    }

    //if time is in milliseconds format
    if (typeof time === 'object') {
        time = time;
    }

    var time_formats = [
        [60, 'seconds', 1], //60
        [120, '1 minute ago', '1 minute from now'], // 60*2
        [3600, 'minutes', 60], // 60*60, 60
        [7200, '1 hour ago', '1 hour from now'], // 60*60*2
        [86400, 'hours', 3600], // 60*60*24, 60*60
        [172800, 'Yesterday', 'Tomorrow'], // 60*60*24*2
        [604800, 'days', 86400], // 60*60*24*7, 60*60*24
        [1209600, 'Last week', 'Next week'], // 60*60*24*7*4*2
        [2419200, 'weeks', 604800], // 60*60*24*7*4, 60*60*24*7
        [4838400, 'Last month', 'Next month'], // 60*60*24*7*4*2
        [29030400, 'months', 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
        [58060800, 'Last year', 'Next year'], // 60*60*24*7*4*12*2
        [2903040000, 'years', 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
        [5806080000, 'Last century', 'Next century'], // 60*60*24*7*4*12*100*2
        [58060800000, 'centuries', 2903040000] // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
    ];
    var seconds = (+new Date() - time) / 1000, //   60*60*24*7*4*12*100
        token = 'ago',
        list_choice = 1; // 1 is 'ago' and 2 is 'from now'

    if (seconds == 0) {
        return 'Just now' //just now
    }
    if (seconds < 0) { //if seconds is less than 0
        seconds = Math.abs(seconds);
        token = 'from now'; //set token to 'from now'
        list_choice = 2; //set list_choice to 2
    }
    var i = 0, format; //format is undefined
    while (format = time_formats[i++]) if (seconds < format[0]) { //if seconds is less than format[0]
        if (typeof format[2] == 'string')   //if format[2] is a string
            return format[list_choice]; //return format[list_choice]
        else
            return Math.floor(seconds / format[2]) + ' ' + format[1] + ' ' + token; //return seconds divided by format[2] and add format[1] and token
    }
    return time; //return time
}

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="User Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">
                                <h5 class="card-title mb-0">User Manager</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3 pt-md-0"
                                v-if="$page.props.user.permissions.includes('add-users')">
                                <div class="dt-buttons">
                                    <Link type="button" class="btn btn-primary" data-bs-toggle="offcanvas"
                                        data-bs-target="#addUserOffcanvas" href="#">
                                    <i class='bx bx-plus bx-tada'></i> Add New User
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-12 col-md-12 d-flex justify-content-center justify-content-md-end">
                                <div class="dataTables_filter">
                                    <label>Search:
                                        <input v-model="userSearch" type="text" class="form-control"
                                            placeholder="Type Username"></label>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>User</th>
                                    <th>Parent</th>
                                    <th>Last Activity</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="user in users.data" :key="user.id">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="avatar-wrapper">
                                                <div class="avatar me-2"><span
                                                        class="avatar-initial rounded-circle bg-label-warning">{{
                                                            getInitials(user.name)
                                                        }}</span>
                                                </div>
                                            </div>
                                            <div class=" d-flex flex-column">
                                                <span class="emp_name text-truncate">{{
                                                    user.name
                                                }}</span>
                                                <small class="emp_post text-truncate text-muted">
                                                    {{ user.email }}
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td v-if="user.user_type == 'Company'">{{ user.company.name }}</td>
                                    <td v-else>{{ user.agent?.agency_name }}</td>
                                    <td>
                                        {{ timeAgo(props.sessions[0].last_activity) }}
                                    </td>
                                    <td>
                                        <span class="badge bg-label-success">Verified--</span>
                                    </td>
                                    <td>
                                        <Link :href="'/users/show/' + user.id" class="btn btn-icon btn-label-secondary"
                                            v-if="$page.props.user.permissions.includes('view-users')" preserve-state>
                                        <i class="bx bx-search-alt"></i>
                                        </Link>
                                        <Link :href="'/bookings/edit/' + user.id"
                                            v-if="$page.props.user.permissions.includes('edit-user')"
                                            class="btn btn-icon btn-label-secondary m-1" preserve-state>
                                        <i class="bx bx-edit"></i>
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
                                    Showing {{ users.from }} to {{ users.to }} of {{
                                        users.total
                                    }} Users
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="users.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="offcanvas offcanvas-end" id="addUserOffcanvas" aria-hidden="true">
                <div class="offcanvas-header mb-3">
                    <h5 class="offcanvas-title">Add User</h5>
                    <button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas"
                        aria-label="Close"></button>
                </div>
                <div class="offcanvas-body flex-grow-1">
                    <form @submit.prevent="UserAddformSubmit">
                        <div class="mb-3">
                            <label class="form-label" for="invoiceAmount">Full Name</label>
                            <div class="input-group">
                                <input type="text" name="name" class="form-control" v-model="UserAddform.name"
                                    placeholder="Full Name" required="required" />
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label" for="paid_receipt">Email Address</label>
                            <input type="text" name="email" class="form-control" v-model="UserAddform.email"
                                placeholder="Email Address">
                        </div>
                        <div class="mb-3">
                            <label class="form-label" for="paid_receipt">Password</label>
                            <input type="password" name="firstpassword" class="form-control" placeholder="Password"
                                v-model="UserAddform.firstpassword">
                        </div>
                        <div class="mb-3">
                            <label class="form-label" for="paid_receipt">Confirm Password</label>
                            <input type="password" name="password" class="form-control" v-model="UserAddform.password"
                                placeholder="Confirm Password" :class="{ 'is-invalid': UserAddform.errors.password }"
                                @change="checkPassword">
                            <div v-if="UserAddform.errors.password" class="invalid-feedback">
                                <div v-text="UserAddform.errors.password">
                                </div>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label" for="agent">User Type</label>
                            <select class="form-select" name="user_type" required="required"
                                v-model="UserAddform.user_type">
                                <option value="" selected disabled>Select Type</option>
                                <option value="Agent">Agent</option>
                                <option value="Company">Company</option>
                            </select>
                        </div>
                        <div class="mb-3">
                            <label class="form-label" for="agent">Agent/Company</label>
                            <select class="form-select" name="type_id" required="required" v-model="UserAddform.type_id">
                                <option value="" selected disabled>Select Agent/Company</option>
                                <option v-if="UserAddform.user_type == 'Agent'" v-for="agent in agents" :value="agent.id">
                                    {{ agent.agency_name }}
                                </option>
                                <option v-if="UserAddform.user_type == 'Company'" v-for="company in companies"
                                    :value="company.id">
                                    {{ company.name }}
                                </option>
                            </select>
                        </div>
                        <div class="mb-3 d-flex flex-wrap">
                            <button type="submit" class="btn btn-primary me-3">Add User</button>
                            <button type="button" class="btn btn-label-secondary"
                                data-bs-dismiss="offcanvas">Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </AppLayout>
</template>